import {Component, computed, DestroyRef, effect, inject, OnInit} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ReactiveFormsModule} from '@angular/forms';
import {finalize} from 'rxjs';
import {NzDescriptionsModule} from 'ng-zorro-antd/descriptions';
import {NzFormModule} from 'ng-zorro-antd/form';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import {AuthFlowService} from '../../../core/services/auth-flow.service';
import {PsdRulesService} from '../../../core/services/psd-rules.service';
import {psdRequirements} from '../../../shared/psd-requirements';
import {PSD_VALIDATORS, psdValidators} from '../../../shared/psd-validator';
import {textControl} from '../../../shared/text-control';

export interface SetPsdModalData {
  userId: string;
  username: string;
}

@Component({
  selector: 'app-set-psd-modal',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    NzDescriptionsModule,
    NzFormModule,
    NzInputModule,
  ],
  templateUrl: './set-psd-modal.html',
  styleUrl: './set-psd-modal.css',
})
export class SetPsdModal implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly _data = inject<SetPsdModalData>(NZ_MODAL_DATA);
  private readonly _modalRef = inject(NzModalRef);
  private readonly _authFlow = inject(AuthFlowService);
  private readonly _notification = inject(NzNotificationService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _psdRules = inject(PsdRulesService);

  readonly username = this._data.username;
  readonly psdControl = textControl(PSD_VALIDATORS);
  readonly requirements = computed(() => psdRequirements(this._psdRules.rules()));

  psdVisible = false;

  constructor() {
    effect(() => {
      this.psdControl.setValidators(psdValidators(this._psdRules.rules()));
      this.psdControl.updateValueAndValidity({emitEvent: false});
    });

    this.psdControl.statusChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._modalRef.updateConfig({nzOkDisabled: this.psdControl.invalid}));
  }

  ngOnInit(): void {
    this._psdRules.load();
  }

  handleOk(): void {
    if (this.psdControl.invalid) {
      this.psdControl.markAsTouched();
      return;
    }

    this._modalRef.updateConfig({nzOkLoading: true, nzCancelDisabled: true});

    this._authFlow.setManagedUserPsd(this._data.userId, this.psdControl.value)
      .pipe(finalize(() => this._modalRef.updateConfig({nzOkLoading: false})), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._modalRef.close(true);
          this._notification.success(this.translate.instant('common.success'), this.translate.instant('adminUsers.psdChanged'));
        },
        error: () => {
          this._modalRef.updateConfig({nzCancelDisabled: false});
        }
      });
  }
}
